import React from 'react';

import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import {useTranslation, TFunction} from 'react-i18next';
import {Route, useRouteMatch, useParams, useHistory} from 'react-router-dom';

import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Back, Container} from 'components/pages/NotFound/styled';
import {
    TitleWrapper,
    ContentColumns,
    TitleText,
    ContentContainer,
    StatusWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {Title} from 'components/ui-kit/Title';
import {IS_CAR} from 'core/constants/env';
import {ProductGroups} from 'core/constants/productGroups';
import {RECEIPT_STATUS_COLORS} from 'core/constants/statuses';
import {CheckStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {commonDataState$, ReceiptsDetailGate} from 'models/documents/checks/detail';
import {Gate, state$ as ProductsState$} from 'models/documents/checks/detail/products/table';
import {ReceiptDetail, ReceiptDetailRaw} from 'types/documents';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {BuyerPage} from './Buyer';
import {Products} from './Products';
import {DataTitle, Error, ErrorsWrapper} from './styled';

/*
    t('pages.documents.receipts.detail.createdAt')
    t('pages.documents.receipts.detail.receiptId')
    t('pages.documents.receipts.detail.user')
    t('pages.documents.receipts.detail.retailAddress')
    t('pages.documents.receipts.detail.userInn')
    t('pages.documents.receipts.detail.operator')
    t('pages.documents.receipts.detail.fiscalDriveNumber')
    t('pages.documents.receipts.detail.taxationType')
    t('pages.documents.receipts.detail.shiftNumber')
    t('pages.documents.receipts.detail.dateTime')
    t('pages.documents.receipts.detail.fiscalDocumentNumber')
    t('pages.documents.receipts.detail.productsTotalCount')
*/
/*
    t('dictionaries.documents.checks.taxationType.1')
    t('dictionaries.documents.checks.taxationType.2')
*/

function getStatusColors(status?: CheckStatus) {
    return {color: RECEIPT_STATUS_COLORS[status ?? 'DEFAULT']};
}

export function convertData(data: ReceiptDetailRaw | null, t: TFunction): ReceiptDetail {
    return {
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        receiptId: handleNil(data?.receiptId),
        user: handleNil(data?.user),
        retailAddress: handleNil(data?.retailAddress),
        userInn: handleNil(data?.userInn),
        operator: handleNil(data?.operator),
        fiscalDriveNumber: handleNil(data?.fiscalDriveNumber),
        taxationType: handleNil(data?.taxationType, (value) =>
            t(`dictionaries.documents.checks.taxationType.${value}`),
        ),
        shiftNumber: handleNil(data?.shiftNumber),
        dateTime: handleNil(data?.dateTime, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        fiscalDocumentNumber: handleNil(data?.fiscalDocumentNumber),
        productsTotalCount: handleNil(data?.productsTotalCount),
        status: handleNil(data?.status, (value: CheckStatus) =>
            t('dictionaries.documents.checks.status.' + value),
        ),
        errors: data?.errors
            ? data.errors.map(
                  (err) => `${t('pages.orders.detail.error')} #${err.code}. ${err.message}`,
              )
            : undefined,
    };
}

export const ReceiptsDetail = () => {
    const {id}: {id: string} = useParams();
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const history = useHistory();

    const {data, isLoading} = useStore(commonDataState$);
    const productState = useStore(ProductsState$);

    const convertedData = React.useMemo(() => convertData(data, t), [data]);

    return (
        <Page title={t('pages.documents.receipts.gridName')}>
            <ReceiptsDetailGate id={id} />
            <BackTo to={ROUTE.DOCUMENTS_CHECKS}>{t('pages.documents.back')}</BackTo>
            {data && (
                <ContentContainer status={isLoading ? 'loading' : 'default'}>
                    <Gate receiptId={data.receiptId} />
                    <TitleWrapper>
                        <TitleText>
                            {`${t('pages.documents.receipts.receiptId')} ${
                                convertedData.fiscalDocumentNumber
                            }`}
                        </TitleText>
                        <StatusWrapper {...getStatusColors(data?.status)}>
                            {convertedData.status}
                        </StatusWrapper>
                    </TitleWrapper>

                    {convertedData.errors && (
                        <ErrorsWrapper>
                            {convertedData.errors.map((err, index) => (
                                <Error key={index}>{err}</Error>
                            ))}
                        </ErrorsWrapper>
                    )}

                    <div style={{padding: '15px 0 40px 0'}}>
                        <ContentColumns
                            data={[
                                {
                                    receiptId: convertedData.receiptId,
                                },
                                {
                                    createdAt: convertedData.createdAt,
                                },
                                {
                                    productsTotalCount: convertedData.productsTotalCount,
                                },
                            ]}
                            tNameSpace="pages.documents.receipts.detail"
                        />
                    </div>

                    <RouterTabList baseUrl={url}>
                        <RouterTab url="">{t('pages.documents.detail.commonInfo')}</RouterTab>
                        <RouterTab url="products">{t('pages.documents.detail.products')}</RouterTab>
                        {IS_CAR &&
                            !!productState.data.length &&
                            productState.data.every(
                                (p) => p.productGroupCode === ProductGroups.MEDICATIONS,
                            ) &&
                            data.status === CheckStatus.PROCESSED && (
                                <RouterTab url="buyer">
                                    {t('pages.documents.detail.buyer')}
                                </RouterTab>
                            )}
                    </RouterTabList>

                    <RouterSwitch rootPath={path}>
                        <Route path={`${path}`} exact>
                            <Grid container>
                                <Grid item xs={12} md={4}>
                                    <DataTitle>{t('pages.documents.detail.seller')}</DataTitle>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DataTitle>{t('pages.documents.detail.fiscalData')}</DataTitle>
                                </Grid>
                            </Grid>
                            <ContentColumns
                                data={[
                                    {
                                        user: convertedData.user,
                                        userInn: convertedData.userInn,
                                        taxationType: convertedData.taxationType,
                                        retailAddress: convertedData.retailAddress,
                                        operator: convertedData.operator,
                                        shiftNumber: convertedData.shiftNumber,
                                    },
                                    {
                                        fiscalDocumentNumber: convertedData.fiscalDocumentNumber,
                                        dateTime: convertedData.dateTime,
                                        fiscalDriveNumber: convertedData.fiscalDriveNumber,
                                    },
                                ]}
                                tNameSpace="pages.documents.receipts.detail"
                            />
                        </Route>
                        <Route path={`${path}/products`} exact>
                            <Products receiptId={convertedData.receiptId} />
                        </Route>
                        <Route path={`${path}/buyer`} exact>
                            <BuyerPage />
                        </Route>
                    </RouterSwitch>
                </ContentContainer>
            )}
            {!data && !isLoading && (
                <Container>
                    <Title size={2}>{t('errors.tryMessage')}</Title>
                    <Back onClick={history.goBack}>{t('errors.goBack')}</Back>
                </Container>
            )}
        </Page>
    );
};
